import React, { useState, useEffect, useRef } from 'react';
import ClientSidebar from '../components/ClientSidebar';
import NotificationBell from '../components/NotificationBell';
import { useLocation } from 'react-router-dom';
import html2canvas from 'html2canvas';
import ChatBotTrigger from '../../components/ChatBotTrigger';

import { API_BASE as API_BASE_URL } from '../../config/runtime';

const getStoredClientUser = () => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (parsed?.role || '').toLowerCase() === 'client' ? parsed : null;
  } catch {
    return null;
  }
};

const normalizeStatus = (status) => {
  if (!status) return '';
  const s = status.trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// ─── Receipt Modal (same as ClientPurchased) ───────────────────────────────
const ReceiptModal = ({ booking, onClose, clientUser }) => {
  const services = booking.services || [];
  const numClients = booking.number_of_clients || 1;
  const clientName = clientUser?.full_name || booking.guest_name || 'Client';
  const receiptDate = new Date().toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short',
    day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
  const singleServicePrice = services.length > 0 ? services[0].price : booking.service_price || 0;
  const receiptId = booking.receipt_code || String(booking.booking_id).padStart(7, '0');
  const totalPriceNumber = Number(booking.total_price || (Number(singleServicePrice) * numClients) || 0);
  const amountPaidNumber = Number(booking.amount_paid || 0);
  const amountDueNumber = Number(booking.amount_due ?? Math.max(0, totalPriceNumber - amountPaidNumber));
  const paymentLabel = booking.payment_method === 'qrph' ? 'QR Ph' : (booking.payment_method || 'On-Site Payment');
  const totalPrice = totalPriceNumber.toLocaleString();
  const receiptRef = useRef(null);
  const uploadOnceRef = useRef(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  useEffect(() => {
    let isMounted = true;
    if (uploadOnceRef.current || isUploadingReceipt) return () => { isMounted = false; };
    const uploadReceiptImage = async () => {
      if (!receiptRef.current) return;
      uploadOnceRef.current = true;
      setIsUploadingReceipt(true);
      try {
        await new Promise((resolve) => setTimeout(resolve, 60));
        const canvas = await captureReceiptCanvas(2);
        if (!canvas) throw new Error('Failed to generate receipt image');
        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Failed to generate receipt image');
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        await fetch(`${API_BASE_URL}/upload_receipt_image.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_id: booking.booking_id, image_data: dataUrl })
        });
      } catch (err) {
        console.error('Failed to upload receipt image:', err);
      } finally {
        if (isMounted) setIsUploadingReceipt(false);
      }
    };
    uploadReceiptImage();
    return () => { isMounted = false; };
  }, [booking.booking_id, isUploadingReceipt]);

  const formatMoney = (value) => Number(value || 0).toLocaleString();
  const formatReceiptDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const formatReceiptTime = (timeStr) => {
    if (!timeStr) return '-';
    const [hour, minute] = timeStr.split(':');
    const temp = new Date();
    temp.setHours(Number(hour || 0), Number(minute || 0), 0, 0);
    return temp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };
  const formatDuration = (totalMinutes) => {
    if (!totalMinutes) return '-';
    if (totalMinutes < 60) return `${totalMinutes} mins`;
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    if (mins === 0) return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'} ${mins} mins`;
  };
  const totalDurationMinutes = services.length > 0
    ? services.reduce((sum, svc) => sum + Number(svc.duration_minutes || 0), 0)
    : Number(booking.duration_minutes || 0);

  const captureReceiptCanvas = async (scale) => {
    if (!receiptRef.current) return null;
    const element = receiptRef.current;
    const prevStyles = {
      maxHeight: element.style.maxHeight, height: element.style.height,
      overflow: element.style.overflow, overflowY: element.style.overflowY
    };
    const prevScrollTop = element.scrollTop;
    element.style.maxHeight = 'none';
    element.style.height = 'auto';
    element.style.overflow = 'visible';
    element.style.overflowY = 'visible';
    element.scrollTop = 0;
    try {
      await new Promise((resolve) => setTimeout(resolve, 50));
      return await html2canvas(element, { scale, backgroundColor: '#ffffff', useCORS: true });
    } finally {
      element.style.maxHeight = prevStyles.maxHeight;
      element.style.height = prevStyles.height;
      element.style.overflow = prevStyles.overflow;
      element.style.overflowY = prevStyles.overflowY;
      element.scrollTop = prevScrollTop;
    }
  };

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    try {
      const canvas = await captureReceiptCanvas(1);
      if (!canvas) return;
      const downloadImage = (blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Hygea-Receipt-${receiptId}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      };
      if (canvas.toBlob) {
        canvas.toBlob(downloadImage, 'image/png');
      } else {
        const dataUrl = canvas.toDataURL('image/png');
        const response = await fetch(dataUrl);
        downloadImage(await response.blob());
      }
    } catch (err) {
      console.error('Failed to download receipt image:', err);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div ref={receiptRef} className="w-full max-w-sm mx-auto bg-[#f6f5f2] text-gray-800 font-mono shadow-2xl border border-gray-300 rounded-lg max-h-[95vh] overflow-y-auto">
        <div className="px-6 py-6">
          <div className="text-center">
            <div className="text-lg font-bold tracking-[0.3em]">HYGEA WELLNESS</div>
            <div className="text-[11px] text-gray-600">Branch: {booking.branch_name || 'Main Branch'}</div>
            <div className="text-[11px] text-gray-600">Address: {booking.branch_address || '-'}</div>
            <div className="text-[11px] text-gray-600">Receipt #{receiptId}</div>
            <div className="text-[11px] text-gray-600">{receiptDate}</div>
          </div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-center text-sm font-bold tracking-[0.3em]">APPOINTMENT RECEIPT</div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-[11px] text-gray-500">
            <div>Client: {clientName}</div>
            <div>No. of Person/s: {numClients}</div>
            <div>Appointment: {formatReceiptDate(booking.appointment_date)} {formatReceiptTime(booking.appointment_time)}</div>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-[11px] font-bold uppercase tracking-widest text-gray-600">
            <span>Description</span><span>Amount</span>
          </div>
          <div className="my-2 border-t border-dashed border-gray-300" />
          <div className="space-y-3">
            {services.length > 0 ? services.map((svc, idx) => {
              const lineTotal = Number(svc.price || 0) * numClients;
              return (
                <div key={idx}>
                  <div className="flex justify-between text-sm">
                    <span className="font-semibold">{svc.service_name}</span>
                    <span className="font-semibold">PHP {formatMoney(lineTotal)}</span>
                  </div>
                  <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(svc.duration_minutes || 0))}</div>
                </div>
              );
            }) : (
              <div>
                <div className="flex justify-between text-sm">
                  <span className="font-semibold">{booking.service_name || 'Service'}</span>
                  <span className="font-semibold">PHP {formatMoney(Number(singleServicePrice || 0) * numClients)}</span>
                </div>
                <div className="text-[10px] text-gray-500">Duration: {formatDuration(Number(booking.duration_minutes || 0))}</div>
              </div>
            )}
          </div>
          <div className="my-4 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Total Duration</span><span>{formatDuration(totalDurationMinutes)}</span>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-sm font-semibold text-gray-600">
            <span>Amount Paid (50%)</span><span>PHP {formatMoney(amountPaidNumber)}</span>
          </div>
          <div className="flex justify-between text-sm font-semibold text-gray-600 mt-2">
            <span>Remaining Balance</span><span>PHP {formatMoney(amountDueNumber)}</span>
          </div>
          <div className="my-3 border-t border-dashed border-gray-300" />
          <div className="flex justify-between text-lg font-bold">
            <span>Total</span><span>PHP {totalPrice}</span>
          </div>
          <div className="text-[10px] text-gray-500 mt-2">Payment: {paymentLabel}</div>
          <div className="text-center text-xs tracking-[0.3em] text-gray-500 my-4">************************</div>
          <div className="text-center text-sm font-bold tracking-[0.4em]">THANK YOU!</div>
          <div className="text-center text-[10px] text-gray-500 mt-4">Hygea Wellness - All Rights Reserved (c) 2024</div>
          <div className="flex flex-col gap-3 justify-center mt-6" data-html2canvas-ignore="true">
            <button
              onClick={handleDownload}
              className="px-6 py-3 bg-gray-900 hover:bg-black text-white font-bold uppercase text-sm tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Download Receipt
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-[#B89F80] hover:bg-[#9a8368] text-white font-bold uppercase text-sm tracking-wide rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────
const ClientHistory = () => {
  const clientUser = getStoredClientUser();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [highlightedBookingId, setHighlightedBookingId] = useState(null);
  const [selectedReceiptBooking, setSelectedReceiptBooking] = useState(null);

  useEffect(() => {
    if (!clientUser?.user_id) {
      setLoading(false);
      setError('User not logged in');
      return;
    }

    const fetchBookings = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await fetch(`${API_BASE_URL}/get_user_bookings.php?user_id=${clientUser.user_id}`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server error ${response.status}: ${errorText.slice(0, 200)}`);
        }
        let data;
        try {
          data = JSON.parse(await response.text());
        } catch {
          throw new Error('Invalid server response (not JSON)');
        }
        if (data.success) {
          const bookingsData = Array.isArray(data.bookings) ? data.bookings : [];
          setBookings(bookingsData.map((booking) => ({
            ...booking,
            status: normalizeStatus(booking?.status),
          })));
        } else {
          setError(data.message || 'Failed to load bookings');
        }
      } catch (err) {
        if (err.name === 'TypeError' && err.message.includes('fetch')) {
          setError('Network unavailable. Check connection and XAMPP server.');
        } else {
          setError(err.message || 'Failed to load bookings.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [clientUser?.user_id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightParam = params.get('highlight');
    if (!highlightParam) { setHighlightedBookingId(null); return; }
    setHighlightedBookingId(highlightParam);
    const scrollTimer = setTimeout(() => {
      const card = document.getElementById(`booking-${highlightParam}`);
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    const clearTimer = setTimeout(() => setHighlightedBookingId(null), 5000);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [location.search, bookings.length]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return 'N/A';
    const [hour, minute] = timeStr.split(':');
    const temp = new Date();
    temp.setHours(Number(hour || 0), Number(minute || 0), 0, 0);
    return temp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'approved':  return 'bg-emerald-100 text-emerald-700';
      case 'pending':   return 'bg-amber-100 text-amber-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'declined':  return 'bg-red-100 text-red-700';
      case 'cancelled': return 'bg-rose-100 text-rose-700';
      default:          return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredBookings = Array.isArray(bookings) ? bookings.filter((booking) => {
    if (!booking) return false;
    if (filter === 'all') return true;
    return (booking.status || '').toLowerCase() === filter.toLowerCase();
  }) : [];

  const stats = {
    total:     bookings.length,
    completed: bookings.filter((b) => (b?.status || '').toLowerCase() === 'completed').length,
    cancelled: bookings.filter((b) => (b?.status || '').toLowerCase() === 'cancelled').length,
  };

  return (
    <div className="min-h-screen hygea-app-bg">
      <div className="fixed top-4 right-6 z-50">
        <NotificationBell isScrolled={false} forceBrown={true} />
      </div>
      <ClientSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        clientName={clientUser?.full_name}
        clientEmail={clientUser?.email}
        clientAvatar={clientUser?.avatar_url}
        onLogout={() => { window.location.href = '/'; }}
      />

      <div className="transition-all duration-300">
        <div className="p-8">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-800">Booking History</h2>
              <p className="text-gray-500 mt-1">View your complete booking history</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase">Total Bookings</p>
                {loading ? <div className="h-8 bg-gray-100 rounded animate-pulse mt-1" /> : (
                  <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                )}
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase">Completed</p>
                {loading ? <div className="h-8 bg-gray-100 rounded animate-pulse mt-1" /> : (
                  <p className="text-2xl font-bold text-blue-600">{stats.completed}</p>
                )}
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-200">
                <p className="text-xs font-bold text-gray-400 uppercase">Cancelled</p>
                {loading ? <div className="h-8 bg-gray-100 rounded animate-pulse mt-1" /> : (
                  <p className="text-2xl font-bold text-rose-500">{stats.cancelled}</p>
                )}
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-6 flex-wrap">
              {['all', 'completed', 'approved', 'pending', 'declined', 'cancelled'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  disabled={loading}
                  className={`px-4 py-2 rounded-xl text-sm font-bold transition ${
                    filter === status
                      ? 'bg-[#b37b65] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  } disabled:opacity-50`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Content */}
            {error ? (
              <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700">
                <p className="font-medium">{error}</p>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b37b65] mx-auto mb-4"></div>
                <p className="text-gray-500">Loading bookings...</p>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-500">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking, index) => {
                  const bookingKey = booking?.booking_id ?? booking?.id ?? index;
                  const isHighlighted = highlightedBookingId === String(bookingKey);
                  const normalizedSt = normalizeStatus(booking?.status);
                  const canViewReceipt = ['Pending', 'Approved', 'Completed'].includes(normalizedSt)
                    && Number(booking?.amount_paid || 0) > 0;

                  // Determine service display name (multi-service vs single)
                  const serviceDisplay = Array.isArray(booking?.services) && booking.services.length > 0
                    ? booking.services.map((s) => s.service_name).join(', ')
                    : booking?.service_name || booking?.service || 'N/A';

                  return (
                    <div
                      key={bookingKey}
                      id={`booking-${bookingKey}`}
                      className={`overflow-hidden rounded-2xl border shadow-sm transition bg-white ${
                        isHighlighted
                          ? 'border-[#b37b65] bg-[#fffaf5] shadow-lg ring-2 ring-[#b37b65]/50'
                          : normalizedSt === 'Completed'
                          ? 'border-blue-100'
                          : 'border-gray-200'
                      }`}
                    >
                      {/* Completed banner */}
                      {normalizedSt === 'Completed' && (
                        <div className="bg-blue-50 border-b border-blue-100 px-6 py-2 flex items-center gap-2">
                          <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs font-bold text-blue-600 uppercase tracking-widest">Completed</span>
                        </div>
                      )}

                      <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-gray-800">{serviceDisplay}</h3>
                            <p className="text-sm text-gray-500">
                              {booking?.therapist_name || booking?.therapist || ''}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-2xl font-bold text-[#6f4a42]">
                              ₱{Number(booking?.total_price || booking?.price || 0).toLocaleString()}
                            </span>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Cost</p>
                          </div>
                        </div>

                        <div className="grid grid-cols-5 gap-4 py-4 border-t border-b border-gray-100 mb-6">
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Branch</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {booking?.branch_name || booking?.branch || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Date</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {formatDate(booking?.appointment_date || booking?.date)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Time</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {formatTime(booking?.appointment_time || booking?.time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Amount Paid (50%)</p>
                            <p className="text-sm font-semibold text-gray-700">
                              ₱{Number(booking?.amount_paid || 0).toLocaleString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase">Status</p>
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase ${getStatusColor(booking?.status)}`}>
                              {normalizedSt || 'Unknown'}
                            </span>
                          </div>
                        </div>

                        {/* View Receipt button for Approved and Completed */}
                        {canViewReceipt && (
                          <div className="flex justify-end">
                            <button
                              onClick={() => setSelectedReceiptBooking(booking)}
                              className="px-6 py-2 bg-blue-500 text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition-all shadow-md hover:shadow-lg"
                            >
                              View Receipt
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {selectedReceiptBooking && (
        <ReceiptModal
          booking={selectedReceiptBooking}
          clientUser={clientUser}
          onClose={() => setSelectedReceiptBooking(null)}
        />
      )}
      <ChatBotTrigger />
    </div>
  );
};

export default ClientHistory;
